import { useState } from "react";
import { socialStars } from "@/mocks/influencers";

const platforms = ["All", "YouTube", "TikTok", "Facebook", "Twitter"];

export default function SocialStarsSection() {
  const [activePlatform, setActivePlatform] = useState("All");
  const featured = socialStars.find((s) => s.featured);
  const filtered = socialStars.filter((s) =>
    !s.featured && (activePlatform === "All" || s.platform === activePlatform)
  );

  const platformMeta: Record<string, { icon: string; color: string }> = {
    YouTube: { icon: "ri-youtube-fill", color: "text-red-500" },
    TikTok: { icon: "ri-tiktok-fill", color: "text-white" },
    Facebook: { icon: "ri-facebook-circle-fill", color: "text-sky-400" },
    Twitter: { icon: "ri-twitter-x-fill", color: "text-white" },
  };

  return (
    <section id="social-stars" className="py-16 bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-amber-400 text-sm font-semibold uppercase tracking-wider">Community Stars</span>
          <h2 className="text-2xl md:text-3xl font-bold text-white mt-1">Facebook · TikTok · Twitter · YouTube Stars</h2>
          <p className="text-gray-400 text-sm mt-2">Follow top career coaches, finance gurus, dropship kings, and business mentors</p>
        </div>

        {/* Platform Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {platforms.map((p) => (
            <button
              key={p}
              onClick={() => setActivePlatform(p)}
              className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activePlatform === p
                  ? "bg-amber-500 text-white"
                  : "bg-white/10 hover:bg-white/20 text-white"
              }`}
            >
              {p !== "All" && <i className={`${platformMeta[p]?.icon} text-sm`}></i>}
              {p}
            </button>
          ))}
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-8">
          {/* Featured Card */}
          {featured && (
            <div className="lg:col-span-2 relative rounded-2xl overflow-hidden cursor-pointer group h-80 lg:h-auto min-h-80">
              <img
                src={featured.avatar}
                alt={featured.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent flex flex-col justify-end p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className={`${platformMeta[featured.platform]?.icon || "ri-youtube-fill"} text-red-400 text-lg`}></i>
                  <span className="text-white/70 text-xs">{featured.platform} Star</span>
                </div>
                <h3 className="text-white text-2xl font-bold mb-1">{featured.name}</h3>
                <span className="inline-block bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full mb-2 w-fit">
                  {featured.specialty}
                </span>
                <p className="text-white/70 text-xs mb-3">{featured.bio}</p>
                <div className="flex items-center gap-5 mb-4">
                  <div className="text-white">
                    <div className="font-bold text-xl">{featured.followers}</div>
                    <div className="text-white/60 text-xs">Followers</div>
                  </div>
                  <div className="text-white">
                    <div className="font-bold text-xl">{featured.engagement}</div>
                    <div className="text-white/60 text-xs">Engagement</div>
                  </div>
                </div>
                <button className="border border-white/50 hover:bg-amber-500 hover:border-amber-500 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap w-fit">
                  Follow Now
                </button>
              </div>
            </div>
          )}

          {/* Other Stars Grid */}
          <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {(filtered.length > 0 ? filtered : socialStars.filter((s) => !s.featured)).map((star) => (
              <div
                key={star.id}
                className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col items-center text-center hover:bg-white/10 hover:border-amber-400/40 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-full overflow-hidden mb-3 ring-2 ring-white/20 group-hover:ring-amber-400 transition-all flex-shrink-0">
                  <img src={star.avatar} alt={star.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <i className={`${platformMeta[star.platform]?.icon || "ri-global-line"} text-xs ${platformMeta[star.platform]?.color || "text-white"}`}></i>
                  <span className="text-white font-semibold text-sm">{star.name}</span>
                </div>
                <span className="text-amber-400 text-xs font-medium mb-1">{star.specialty}</span>
                <p className="text-gray-400 text-xs mb-2 line-clamp-2">{star.bio}</p>
                <div className="text-white font-bold text-base">{star.followers}</div>
                <div className="text-gray-500 text-xs mb-3">Followers · {star.engagement} eng.</div>
                <button className="text-xs font-semibold px-4 py-1.5 rounded-full border border-white/30 text-white hover:bg-amber-500 hover:border-amber-500 transition-colors cursor-pointer whitespace-nowrap w-full">
                  Follow
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: "ri-youtube-fill", color: "text-red-400", label: "YouTube Stars", value: "120+" },
            { icon: "ri-tiktok-fill", color: "text-white", label: "TikTok Stars", value: "85+" },
            { icon: "ri-facebook-circle-fill", color: "text-sky-400", label: "Facebook Stars", value: "200+" },
            { icon: "ri-twitter-x-fill", color: "text-white", label: "Twitter Stars", value: "150+" },
          ].map((s) => (
            <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 flex-shrink-0">
                <i className={`${s.icon} ${s.color} text-lg`}></i>
              </div>
              <div>
                <div className="text-white font-bold text-lg">{s.value}</div>
                <div className="text-gray-400 text-xs">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
